import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, Loader2, ShieldAlert, ShieldCheck, Calculator, FlaskConical, CircleDashed } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A', bg: '#F5F7FA' };

const BASIS_STYLE = {
  measured: { color: C.green, Icon: ShieldCheck, label: 'Measured' },
  derived: { color: C.blue, Icon: Calculator, label: 'Derived' },
  synthetic: { color: C.red, Icon: FlaskConical, label: 'Synthetic' },
  declared: { color: C.slate, Icon: ShieldAlert, label: 'Declared' },
  not_assessed: { color: C.slate, Icon: CircleDashed, label: 'Not assessed' },
};

export const DataProvenancePanel = ({ api }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    try {
      const { data } = await api.get(`/enrichment/provenance${refresh ? '?refresh=true' : ''}`);
      setReport(data);
    } catch (err) { console.error('API error:', err?.message); }
    setLoading(false);
    setRefreshing(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (loading) return <Skeleton className="h-64 rounded-sm" data-testid="provenance-loading" />;
  if (!report) return null;

  const byBasis = report.fields_by_basis || {};

  return (
    <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="data-provenance-panel"
      style={{ borderLeft: `3px solid ${C.red}` }}>
      <div className="p-5 pb-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="max-w-3xl">
            <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
              Data Provenance — what these values actually are
            </h3>
            <p className="text-xs mt-1" style={{ color: C.slate }}>
              Counted across {(report.total_entities || 0).toLocaleString()} active entities, not claimed.
              A <strong>measured</strong> value was read from a real source and that source is named.
              A <strong>derived</strong> value was computed by a documented rule. A <strong>synthetic</strong> value
              was manufactured here — a hash of the address or a random draw — because no feed is connected.
            </p>
          </div>
          <Button onClick={() => { setRefreshing(true); load(true); }} disabled={refreshing}
            data-testid="provenance-refresh-btn" className="rounded-sm text-xs" style={{ background: C.blue }}>
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Recount
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
          {['measured', 'derived', 'synthetic', 'declared', 'not_assessed'].map(b => {
            const s = BASIS_STYLE[b];
            return (
              <div key={b} className="p-3 rounded-sm" style={{ background: C.bg }} data-testid={`provenance-count-${b}`}>
                <div className="flex items-center gap-1.5">
                  <s.Icon className="w-3.5 h-3.5" style={{ color: s.color }} />
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{s.label}</p>
                </div>
                <p className="text-lg font-bold mt-0.5" style={{ color: s.color }}>{byBasis[b] || 0}</p>
                <p className="text-[10px]" style={{ color: C.slate }}>fields</p>
              </div>
            );
          })}
        </div>

        {(report.priced_on_synthetic || []).length > 0 && (
          <p className="text-xs mt-4 p-3 rounded-sm" style={{ background: 'rgba(226,75,74,0.07)', color: C.red }}
            data-testid="provenance-priced-warning">
            <strong>{report.priced_on_synthetic.join(', ').replace(/_/g, ' ')}</strong> move the premium on a
            manufactured value. Every quote they touch carries a disclosure line in its audit trail, and a
            synthetic flood zone can no longer refer a submission on its own (rule D4 records
            <code className="mx-1">D4_suppressed</code> instead).
          </p>
        )}
        {report.ruling && (
          <p className="text-[11px] mt-3 pl-3" style={{ color: C.slate, borderLeft: `2px solid ${C.amber}` }}
            data-testid="provenance-ruling">{report.ruling}</p>
        )}
      </div>

      <div className="border-t border-[rgba(136,150,168,0.15)]">
        <table className="w-full table-fixed">
          <colgroup>
            <col style={{ width: '20%' }} /><col style={{ width: '50%' }} /><col style={{ width: '30%' }} />
          </colgroup>
          <thead>
            <tr style={{ background: C.bg }}>
              {['Field', 'How the value is produced', 'What it does'].map(h => (
                <th key={h} className="px-4 py-2 text-[10px] uppercase tracking-wider font-semibold text-left"
                  style={{ color: C.slate }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(report.fields || []).map(f => {
              const s = BASIS_STYLE[f.basis] || BASIS_STYLE.declared;
              const moves = /premium|refer/.test(f.effect || '');
              return (
                <tr key={f.field} className="border-t border-[rgba(136,150,168,0.1)] hover:bg-[#FAFBFC]"
                  data-testid={`provenance-row-${f.field}`}>
                  <td className="px-4 py-3 align-top">
                    <p className="text-[12px] font-medium break-words" style={{ color: C.navy }}>
                      {f.field.replace(/_/g, ' ')}
                    </p>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase inline-block mt-1"
                      style={{ background: s.color + '15', color: s.color }}>{s.label}</span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <p className="text-[11px] break-words" style={{ color: C.navy }}>{f.method}</p>
                    <p className="text-[11px] break-words mt-0.5" style={{ color: C.slate }}>
                      Source: {f.source}
                    </p>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <p className="text-[11px] break-words" style={{ color: moves ? C.amber : C.slate }}>{f.effect}</p>
                    <p className="text-[10px] font-mono mt-0.5" style={{ color: C.slate }}>
                      on {f.pct}% of the register
                    </p>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default DataProvenancePanel;
